/**
 * "Where did this row come from?" badge — the schema whose table the row was
 * read from, plus the name of the user who created it.
 *
 * Two chips rather than one string so the schema keeps the indigo pill the rest
 * of the app already uses for a foreign-schema row (ResultsTable's name-cell
 * badge, RosterTable's), and the creator reads as secondary next to it.
 *
 * Deliberately INLINE-STYLED, not Tailwind: this renders both inside
 * `#clinic-root` (MedStudentClinicPage — Tailwind utilities are scoped to that
 * subtree) and in a plain Chakra page (PatientPage), and a utility-class
 * version would silently render unstyled in the second. The light chip reads
 * fine against the clinic header in dark mode as well.
 */
import React, { useEffect, useState } from 'react';
import { fetchRowCreatorName } from '../utils/rowCreator';

const CHIP = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.4,
  padding: '3px 8px',
  borderRadius: '5px',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

const SCHEMA_CHIP = { ...CHIP, backgroundColor: '#e0e7ff', color: '#4338ca' };
// No max-width / ellipsis: the creator chip shows the FULL name. A long one
// wraps the header (both hosts are flex-wrap containers) rather than truncating.
const CREATOR_CHIP = {
  ...CHIP,
  backgroundColor: '#f1f5f9',
  color: '#334155',
  border: '1px solid #e2e8f0',
};

/**
 * @param schema       schema the row was read from (e.g. "MEDSTUD", "public")
 * @param userId       the row's `user_id` — the creator
 * @param table        table the row lives in; only used in the tooltip
 * @param creatorName  pre-resolved name; skips the lookup when supplied
 */
export default function RowOriginBadge({
  schema,
  userId,
  table = 'notes-c',
  creatorName = null,
}) {
  const [resolved, setResolved] = useState(creatorName);
  // null = still looking / nothing to look up; '' = looked and came back empty.
  const [settled, setSettled] = useState(Boolean(creatorName) || !userId);

  useEffect(() => {
    if (creatorName) {
      setResolved(creatorName);
      setSettled(true);
      return undefined;
    }
    if (!userId) {
      setResolved(null);
      setSettled(true);
      return undefined;
    }
    let cancelled = false;
    setSettled(false);
    (async () => {
      const name = await fetchRowCreatorName(userId, schema);
      if (cancelled) return;
      setResolved(name);
      setSettled(true);
    })();
    return () => { cancelled = true; };
  }, [userId, schema, creatorName]);

  if (!schema) return null;

  return (
    <span
      data-testid="row-origin-badge"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
    >
      <span style={SCHEMA_CHIP} title={`This row lives in ${schema}."${table}"`}>
        {schema}
      </span>
      {/* The name alone — the tooltip carries the "created by" wording, so the
          chip stays as short as the name itself. Held back until the lookup
          settles so the header doesn't flash "Unknown" on every chart open
          before the name arrives. */}
      {settled && userId && (
        <span
          style={CREATOR_CHIP}
          title={resolved ? `Created by ${resolved}` : `Created by user ${userId}`}
        >
          {resolved || 'Unknown'}
        </span>
      )}
    </span>
  );
}
